import { useEffect, useState } from 'react';

import DualPaneLayout from '@shared/components/DualPaneLayout';
import SinglePaneLayout from '@shared/components/SinglePaneLayout';
import { useAnalytics } from '@shared/hooks/useAnalytics';

import type { SignUpMethods, SignUpSteps } from './constants';
import { SIGN_UP_STEP_CONFIG } from './constants';
import { getInvitationId } from './invitation';
import type { ConsentData } from './sign_up/components/ConsentStep';
import ConsentStep from './sign_up/components/ConsentStep';
import MethodStep from './sign_up/components/MethodStep';
import RightPaneContent from './sign_up/components/RightPaneContent';
import StepContent from './sign_up/components/StepContent';
import VerifyStep from './sign_up/components/VerifyStep';

const SignUp = () => {
  const { trackEvent } = useAnalytics('authentication');

  const [currentStep, setCurrentStep] = useState<SignUpSteps>('method');
  const [stepHistory, setStepHistory] = useState<SignUpSteps[]>(['method']);
  const [selectedMethod, setSelectedMethod] = useState<SignUpMethods | undefined>(undefined);
  const [consentData, setConsentData] = useState<ConsentData | undefined>(undefined);

  const invitationId = getInvitationId();

  useEffect(() => {
    trackEvent('page_view', { page: `signup_${currentStep}` });
  }, [currentStep, trackEvent]);

  const goToStep = (step: SignUpSteps) => {
    setStepHistory([...stepHistory, step]);
    setCurrentStep(step);
  }

  const handleBack = () => {
    trackEvent('signup_back_click', { from_step: currentStep });

    const newHistory = [...stepHistory];
    newHistory.pop();

    const previousStep = newHistory[newHistory.length - 1];

    setStepHistory(newHistory);
    setCurrentStep(previousStep);

    if (previousStep === 'method') {
      setSelectedMethod(undefined);
    }
  }

  const stepComponent = {
    method: () => (
      <MethodStep
        onContinueWithGoogleClick={() => {
          trackEvent('signup_method_google_click');
          setSelectedMethod('google');
          goToStep('consent');
        }}
        onContinueWithEmailClick={() => {
          trackEvent('signup_method_email_click');
          setSelectedMethod('email');
          goToStep('consent');
        }}
      />
    ),
    consent: () => (
      <ConsentStep
        signUpMethod={selectedMethod}
        invitationId={invitationId}
        onContinue={(data: ConsentData) => {
          setConsentData(data);

          if (selectedMethod === 'email') {
            goToStep('verify');
          }
        }}
      />
    ),
    verify: () => (
      <VerifyStep email={consentData.email} />
    ),
  };

  const { layout: currentLayout, getTitle } = SIGN_UP_STEP_CONFIG[currentStep];

  const content = (
    <StepContent
      title={getTitle(!!invitationId)}
      currentStep={currentStep}
      stepComponent={stepComponent}
      stepHistory={stepHistory}
      onBack={handleBack}
    />
  );

  if (currentLayout === 'single') {
    return (
      <SinglePaneLayout content={content} />
    );
  }

  return (
    <DualPaneLayout
      leftContent={content}
      rightContent={<RightPaneContent />}
    />
  );
}

export default SignUp
